/**
 * Chat effects - side effect handlers.
 * 
 * Elm-ish architecture: Side effects are isolated here.
 * The effect runner (useChatProgram.ts) calls these functions.
 * 
 * Security notes:
 * - All API calls use server_readable endpoints only
 * - No client_sealed (E2EE) operations
 * - No encryption keys exposed or manipulated
 * - Safe error messages only (no internal details)
 * - Audit events are emitted by the backend
 */
import {
  listChannels,
  createChannel as apiCreateChannel,
  listMessages,
  sendMessage as apiSendMessage,
  getChannel as apiGetChannel,
  ChatError,
} from '@/api/chat';
import { toChannelDisplay, toMessageDisplay, type ChatChannelDisplay } from './model';
import type { ChatEffect } from './update';

/**
 * Effect handler context - injected by the effect runner.
 */
export interface EffectContext {
  dispatch: (msg: import('./messages').ChatMsg) => void;
}

/**
 * Run a single effect.
 * Returns a cleanup function if needed.
 *
 * Note: realtime lifecycle (WebSocket connect/disconnect) is
 * owned directly by useChatProgram; it does NOT route through
 * this effect runner. Earlier revisions of this ACT exposed
 * ConnectRealtime / DisconnectRealtime cases here, but those
 * paths were dead because ChatEffect's union type never
 * included them — see update.ts.
 */
export async function runEffect(
  effect: ChatEffect,
  context: EffectContext
): Promise<(() => void) | void> {
  switch (effect.type) {
    case 'LoadChannels':
      return runLoadChannels(context);

    case 'LoadChannel':
      return runLoadChannel(context, effect.channelId);

    case 'LoadMessages':
      return runLoadMessages(context, effect.channelId);

    case 'CreateChannel':
      return runCreateChannel(context);

    case 'SendMessage':
      return runSendMessage(context, effect.channelId, effect.content);

    default:
      const _exhaustive: never = effect;
      return;
  }
}

/**
 * Load channels from the API.
 */
async function runLoadChannels(context: EffectContext): Promise<void> {
  context.dispatch({ type: 'ChannelsLoading' });

  try {
    const { channels, hasMore } = await listChannels(50, 0);

    const displayChannels: ChatChannelDisplay[] = channels.map(ch =>
      // CORRECTION01 (R2 fix): propagate the S014 counterpart projection
      // through the list path so the channel list view can show the
      // counterpart name. The ChatHeader on the channel-detail branch
      // reads the same field from the channel-detail load (see
      // runLoadChannel).
      toChannelDisplay(ch.id, ch.created_at, ch.counterpart ? {
        profileId: ch.counterpart.profile_id,
        displayName: ch.counterpart.display_name,
        age: ch.counterpart.age,
        cityOrRegion: ch.counterpart.city_or_region,
        photoUrl: ch.counterpart.photo_url,
      } : undefined)
    );
    
    context.dispatch({
      type: 'ChannelsLoaded',
      channels: displayChannels,
      hasMore,
    });
  } catch (err) {
    const errorMessage = getSafeErrorMessage(err);
    context.dispatch({
      type: 'ChannelsLoadFailed',
      error: errorMessage,
    });
  }
}

/**
 * Load messages for a channel from the API.
 */
/**
 * Load a single channel's metadata (including the S014 counterpart
 * projection). CORRECTION01 (R2 fix): the ChatHeader reads counterpart
 * from this load on the reload path AND on the navigation path.
 *
 * Fail-closed: if the channel cannot be fetched, dispatch
 * ChannelLoadFailed (the chat header will fall back to the
 * "Conversation" placeholder).
 */
async function runLoadChannel(context: EffectContext, channelId: string): Promise<void> {
  try {
    const channel = await apiGetChannel(channelId);
    const displayChannel = toChannelDisplay(
      channel.id,
      channel.created_at,
      channel.counterpart
        ? {
            profileId: channel.counterpart.profile_id,
            displayName: channel.counterpart.display_name,
            age: channel.counterpart.age,
            cityOrRegion: channel.counterpart.city_or_region,
            photoUrl: channel.counterpart.photo_url,
          }
        : undefined
    );
    context.dispatch({
      type: 'ChannelLoaded',
      channel: displayChannel,
    });
  } catch (err) {
    context.dispatch({
      type: 'ChannelLoadFailed',
      error: getSafeErrorMessage(err),
    });
  }
}

async function runLoadMessages(context: EffectContext, channelId: string): Promise<void> {
  context.dispatch({ type: 'MessagesLoading' });
  
  try {
    const { messages, hasMore } = await listMessages(channelId, 50, 0);
    
    // SECURITY: Do not log message content
    const displayMessages = messages.map(msg =>
      toMessageDisplay(msg.id, msg.content, msg.sender_account_id, msg.created_at)
    );
    
    context.dispatch({
      type: 'MessagesLoaded',
      messages: displayMessages,
      hasMore,
    });
  } catch (err) {
    const errorMessage = getSafeErrorMessage(err);
    context.dispatch({
      type: 'MessagesLoadFailed',
      error: errorMessage,
    });
  }
}

/**
 * Create a new channel via the API.
 */
async function runCreateChannel(context: EffectContext): Promise<void> {
  try {
    const channel = await apiCreateChannel();
    
    const displayChannel = toChannelDisplay(channel.id, channel.created_at);
    
    context.dispatch({
      type: 'ChannelCreated',
      channel: displayChannel,
    });
  } catch (err) {
    const errorMessage = getSafeErrorMessage(err);
    context.dispatch({
      type: 'ChannelCreatedFailed',
      error: errorMessage,
    });
  }
}

/**
 * Send a message to a channel via the API.
 * 
 * SECURITY NOTES:
 * - Content is sent to server for encryption (server_readable mode)
 * - Content is NOT logged in frontend
 * - Server handles encryption before storage
 */
async function runSendMessage(
  context: EffectContext,
  channelId: string,
  content: string
): Promise<void> {
  try {
    // SECURITY: Content is not logged here
    // The API client sends it to the server which handles encryption
    const response = await apiSendMessage(channelId, content);
    
    // Create a local message representation
    // Note: We don't have the sender_account_id from this response
    // In a full implementation, this would come from the session
    const message = {
      id: response.id,
      content,
      senderAccountId: 'local', // Placeholder - would come from session
      createdAt: new Date(response.created_at),
    };
    
    context.dispatch({
      type: 'MessageSent',
      message,
    });
  } catch (err) {
    const errorMessage = getSafeErrorMessage(err);
    context.dispatch({
      type: 'MessageSendFailed',
      error: errorMessage,
    });
  }
}

/**
 * Extract a safe error message from an error.
 * Maps ChatError and generic errors to user-friendly messages.
 * 
 * Security: Never exposes internal error details (stack traces, DB errors, etc.)
 */
export function getSafeErrorMessage(err: unknown): string {
  if (err instanceof ChatError) {
    // Map specific error codes to user-friendly messages
    switch (err.message) {
      case 'authentication_required':
        return 'Please log in to continue.';
      case 'not_authorized':
        return 'You do not have access to this content.';
      case 'channel_not_found':
        return 'The channel could not be found.';
      case 'message_content_required':
        return 'Please enter a message.';
      case 'message_too_long':
        return 'Your message is too long.';
      default:
        // For unknown ChatErrors, check if safe
        if (err.safe) {
          return err.message;
        }
        return 'An error occurred. Please try again.';
    }
  }
  
  // Generic error handling for non-ChatError errors
  // Do not expose internal error details
  return 'An error occurred. Please try again.';
}